import { alpha } from '@mui/material/styles';

import { getConditionalObject } from 'utils/data';

import { MILESTONE_ROADMAP_STATUS } from '../MilestoneRoadmap/MilestoneRoadmap.constants';

const CURRENT_ROW_DARK_TINT_ALPHA = 0.16;
const COMPLETED_GLOW_DARK_ALPHA = 0.4;
const CURRENT_RING_DARK_ALPHA = 0.32;
const HOLLOW_FILL_DARK_ALPHA = 0.4;

// The current-row highlight tints the milestone color. Its `soft` shade is a
// fixed LIGHT hex, so on the dark popover surface it would force unreadable
// light-on-light text — in dark mode tint the vivid `strong` shade over the
// dark surface instead so theme text stays legible for any milestone hue.
const getCurrentRowBackground = ({ strong, soft }, isDark) =>
  isDark ? alpha(strong, CURRENT_ROW_DARK_TINT_ALPHA) : soft;

// The node glows reuse the milestone's fixed LIGHT `soft` shade, which reads as
// a bright near-white halo on the dark popover. In dark mode tint the vivid
// `strong` shade at low alpha so the glow stays subtle and on-hue for any hue.
const getCompletedNodeShadow = ({ strong, soft }, isDark) =>
  `0 2px 6px -1px ${isDark ? alpha(strong, COMPLETED_GLOW_DARK_ALPHA) : soft}`;

const getCurrentNodeRing = ({ strong, soft }, isDark) =>
  `0 0 0 4px ${isDark ? alpha(strong, CURRENT_RING_DARK_ALPHA) : soft}`;

// The upcoming node's hollow core fills with the light `soft` shade; on dark it
// becomes a bright dot, so tint `strong` at low alpha instead.
const getHollowFill = ({ strong, soft }, isDark) =>
  isDark ? alpha(strong, HOLLOW_FILL_DARK_ALPHA) : soft;

// Milestone-colored text needs a shade that reads on the highlight: `deep` on
// the light tint (light mode), the brighter `strong` on the dark tint.
const getCurrentTextColor = ({ strong, deep }, isDark) => (isDark ? strong : deep);

export const getRowStyles =
  (status, colors) =>
  ({ palette }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 10px',
    borderRadius: '12px',
    ...getConditionalObject(status === MILESTONE_ROADMAP_STATUS.CURRENT, () => ({
      backgroundColor: getCurrentRowBackground(colors, palette.mode === 'dark'),
    })),
  });

export const getNodeStyles =
  (status, colors) =>
  ({ palette }) => {
    const isDark = palette.mode === 'dark';
    const base = {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    };

    if (status === MILESTONE_ROADMAP_STATUS.COMPLETED) {
      return {
        ...base,
        backgroundColor: colors.strong,
        color: palette.common.white,
        boxShadow: getCompletedNodeShadow(colors, isDark),
      };
    }

    if (status === MILESTONE_ROADMAP_STATUS.CURRENT) {
      return {
        ...base,
        backgroundColor: palette.background.paper,
        border: `2px solid ${colors.strong}`,
        boxShadow: getCurrentNodeRing(colors, isDark),
      };
    }

    return {
      ...base,
      backgroundColor: palette.background.paper,
      border: `2px solid ${colors.strong}`,
    };
  };

export const getCoreStyles = strong => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: strong,
});

export const getHollowStyles =
  colors =>
  ({ palette }) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: getHollowFill(colors, palette.mode === 'dark'),
  });

export const midStyles = {
  flex: 1,
  minWidth: 0,
};

export const getNameStyles =
  (status, colors) =>
  ({ palette }) => ({
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // Completed rows set no per-status color and the popover is portaled outside
    // the themed color context, so without this the name inherits the body's
    // black and is invisible on the dark surface. text.primary flips per theme.
    color: palette.text.primary,
    ...getConditionalObject(status === MILESTONE_ROADMAP_STATUS.CURRENT, () => ({
      color: getCurrentTextColor(colors, palette.mode === 'dark'),
    })),
    ...getConditionalObject(status === MILESTONE_ROADMAP_STATUS.UPCOMING, () => ({
      color: palette.text.secondary,
    })),
  });

export const metaStyles = ({ palette }) => ({
  display: 'block',
  marginTop: '2px',
  color: palette.text.secondary,
});

export const rightStyles = {
  flexShrink: 0,
  marginLeft: '8px',
  textAlign: 'right',
};

export const getDateStyles =
  status =>
  ({ palette }) => ({
    display: 'block',
    fontVariantNumeric: 'tabular-nums',
    color:
      status === MILESTONE_ROADMAP_STATUS.UPCOMING ? palette.text.disabled : palette.text.primary,
  });

export const getHereStyles =
  colors =>
  ({ palette }) => ({
    display: 'block',
    marginTop: '2px',
    color: getCurrentTextColor(colors, palette.mode === 'dark'),
  });
